import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertTriangle, ArrowLeft, Delete, Eye, EyeOff, Gauge, KeyRound, Moon, Search, ShieldCheck, Sun, UserRound } from 'lucide-react';
import { Button, Card, Input, Mono } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import brandMark from '@/assets/threed/brand-mark.webp';
import citycabLogo from '@/assets/threed/citycab-logo.webp';

/**
 * The sign-in screen.
 *
 * First screen anyone sees, so it has to say what the product is in one look: an
 * operations instrument for a fleet of vehicles, not a marketing page. The form sits
 * on the left at a comfortable reading width; the right half is the dispatch board —
 * the custody chain this whole application exists to record, drawn as a schematic
 * with stamped stage numbers. It carries no live figures, because nothing here is
 * authenticated and a number on a sign-in screen that nobody can trace is worse than
 * no number at all.
 *
 * Security posture: one message for a wrong address and a wrong password alike, so
 * the screen never confirms whether an account exists. A lock-out is different — the
 * caller has already proved they hold the password by that point, so the server tells
 * them how long, and this page repeats it verbatim rather than guessing.
 */

/** The one thing said about bad credentials, whichever half was wrong. */
const CREDENTIALS_MESSAGE = 'That email address and password do not match.';

const DEMO_EMAIL = 'admin@brightonwheels.in';
const DEMO_PASSWORD = 'Bright@2026';

/** Deliberately loose — the server is the authority, this only catches typos. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The head admin's door: one desk, one PIN (owner, 3 Oct 2026: "clear login
 * screen also i only want to see single bright and pin for head admin").
 *
 * It was four chips — Bright Autozone, Bright on Wheels, FAB EV, TVS — each
 * a separate admin sign-in behind the same PIN, chosen before the PIN to say
 * which book to land in. The Autozone admin now opens every book from the
 * switch at the top (`User.allBooks`), so the choice moved inside the app and
 * the door is the PIN alone. The other three desks still exist on the server
 * (`PORTALS`); this screen just no longer offers them.
 */
const HEAD_ADMIN_DESK = 'AUTOZONE';
/** Sunny's own door (26 Oct 2026): the CEO's desk, behind its own PIN. */
const CEO_DESK = 'CEO';
type DeskDoor = typeof HEAD_ADMIN_DESK | typeof CEO_DESK;

const PIN_LENGTH = 4;

/**
 * Everyone's PIN door (owner, 8 Sep 2026: "a four-digit PIN login for
 * everybody — most people who will use this app cannot read well"). The
 * person finds their own name first — their shop, their town — then taps
 * four digits. The phone remembers who they are, so the next time it is the
 * four digits alone.
 */
interface PinIdentity {
  id: string;
  name: string;
  label: string;
  where: string | null;
  book: string;
}

const PIN_IDENTITY_KEY = 'bsa:pin-identity';

function rememberedIdentity(): PinIdentity | null {
  try {
    const raw = window.localStorage.getItem(PIN_IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PinIdentity>;
    if (typeof parsed.id === 'string' && typeof parsed.name === 'string') {
      return {
        id: parsed.id,
        name: parsed.name,
        label: typeof parsed.label === 'string' ? parsed.label : '',
        where: typeof parsed.where === 'string' ? parsed.where : null,
        book: typeof parsed.book === 'string' ? parsed.book : '',
      };
    }
  } catch {
    // Private mode or a stale value — start by asking.
  }
  return null;
}

function rememberIdentity(identity: PinIdentity | null): void {
  try {
    if (identity) window.localStorage.setItem(PIN_IDENTITY_KEY, JSON.stringify(identity));
    else window.localStorage.removeItem(PIN_IDENTITY_KEY);
  } catch {
    // The door still opens; it just is not remembered.
  }
}

/** The four dots and the keypad — one pad for every PIN door on this screen. */
function PinPad({
  pin,
  pending,
  onDigit,
  onBackspace,
}: {
  pin: string;
  pending: boolean;
  onDigit: (digit: string) => void;
  onBackspace: () => void;
}) {
  return (
    <>
      <div
        className="flex items-center justify-center gap-4"
        role="status"
        aria-label={`${pin.length} of ${PIN_LENGTH} digits entered`}
      >
        {Array.from({ length: PIN_LENGTH }, (_, index) => (
          <span
            key={index}
            className={clsx(
              'h-4 w-4 rounded-pill border transition-colors duration-150',
              index < pin.length ? 'border-accent bg-accent' : 'border-line-strong bg-surface',
            )}
          />
        ))}
      </div>

      {/* Large targets — this screen is used on phones at a counter in a hurry. */}
      <div className="mx-auto grid w-full max-w-[16rem] grid-cols-3 gap-2.5">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button
            key={digit}
            type="button"
            disabled={pending}
            onClick={() => onDigit(digit)}
            className="flex h-14 items-center justify-center rounded-control border border-line bg-surface font-data text-title font-semibold text-ink shadow-sm transition-transform duration-100 hover:border-accent active:translate-y-px disabled:opacity-50"
          >
            {digit}
          </button>
        ))}
        <span aria-hidden />
        <button
          type="button"
          disabled={pending}
          onClick={() => onDigit('0')}
          className="flex h-14 items-center justify-center rounded-control border border-line bg-surface font-data text-title font-semibold text-ink shadow-sm transition-transform duration-100 hover:border-accent active:translate-y-px disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          disabled={pending || pin.length === 0}
          onClick={onBackspace}
          aria-label="Delete the last digit"
          className="flex h-14 items-center justify-center rounded-control border border-line bg-surface text-muted shadow-sm transition-transform duration-100 hover:border-accent hover:text-ink active:translate-y-px disabled:opacity-40"
        >
          <Delete aria-hidden className="h-5 w-5" />
        </button>
      </div>
    </>
  );
}

interface FormErrors {
  email?: string;
  password?: string;
}

interface SubmitError {
  message: string;
  /** ISO instant the lock lifts, when the server sent one. */
  lockedUntil?: string;
  tone: 'credentials' | 'blocked' | 'system';
}

/** Reads `{ lockedUntil }` out of the error envelope's details without trusting it. */
function readLockedUntil(details: unknown): string | undefined {
  if (!details || typeof details !== 'object') return undefined;
  const value = (details as { lockedUntil?: unknown }).lockedUntil;
  return typeof value === 'string' ? value : undefined;
}

function toSubmitError(error: unknown): SubmitError {
  if (error instanceof ApiError) {
    // 401 covers an unknown address and a wrong password alike. It is answered
    // with one sentence on purpose.
    if (error.status === 401) return { message: CREDENTIALS_MESSAGE, tone: 'credentials' };

    // 403 is a lock-out or a deactivated account — both only reachable once the
    // password matched, so the server's own wording gives nothing away.
    if (error.status === 403) {
      return {
        message: error.message,
        lockedUntil: readLockedUntil(error.details),
        tone: 'blocked',
      };
    }

    if (error.status === 429) return { message: error.message, tone: 'blocked' };

    return { message: error.message, tone: 'system' };
  }

  return { message: 'Sign in could not be completed. Please try again.', tone: 'system' };
}

/** The message says how many minutes; this says when. */
function formatUnlockTime(iso: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// The dispatch board
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const { login, loginWithPin, loginWithUserPin, isAuthenticated, isLoading } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [pending, setPending] = useState(false);

  // PIN-first sign-in (8 Sep 2026): 'pin' is everyone's door — find your
  // name, tap four digits. 'admin' is the head admin's PIN — one desk, the
  // book chosen inside the app (3 Oct 2026); 'email' is the credentials form
  // behind both, for the office.
  const [mode, setMode] = useState<'pin' | 'email' | 'admin'>('pin');
  // Which owner's desk the admin PIN opens: the head admin's, or the CEO's.
  const [desk, setDesk] = useState<DeskDoor>(HEAD_ADMIN_DESK);
  const [pin, setPin] = useState('');
  const pinPendingRef = useRef(false);
  const [identity, setIdentity] = useState<PinIdentity | null>(() => rememberedIdentity());
  const [who, setWho] = useState('');
  const [matches, setMatches] = useState<PinIdentity[]>([]);
  const [searching, setSearching] = useState(false);
  const whoRef = useRef<HTMLInputElement>(null);

  // "Who are you?" — two letters bring the names; the list follows the typing.
  useEffect(() => {
    if (mode !== 'pin' || identity) return;
    const needle = who.trim();
    if (needle.length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }
    let live = true;
    setSearching(true);
    const timer = window.setTimeout(() => {
      api
        .get<{ data: PinIdentity[] }>('/auth/pin-directory', { q: needle }, { anonymous: true })
        .then((result) => {
          if (live) setMatches(result.data);
        })
        .catch(() => {
          if (live) setMatches([]);
        })
        .finally(() => {
          if (live) setSearching(false);
        });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [who, mode, identity]);

  useEffect(() => {
    if (mode === 'pin' && !identity) whoRef.current?.focus();
  }, [mode, identity]);

  function chooseIdentity(next: PinIdentity | null) {
    setIdentity(next);
    rememberIdentity(next);
    setPin('');
    setSubmitError(null);
    if (!next) {
      setWho('');
      setMatches([]);
    }
  }

  const emailRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  /*
   * The one interactive 3D moment, back where it lives: the whole card tilts a
   * few degrees toward a fine pointer, and its layers sit at different depths
   * so the tilt reads as parallax. One rAF per move, style written directly —
   * React never re-renders for a pointer. Mouse only, motion-safe only.
   */
  const cardRef = useRef<HTMLDivElement>(null);
  const tiltFrame = useRef(0);
  useEffect(() => {
    const card = cardRef.current;
    const stage = card?.parentElement;
    if (!card || !stage) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const rim = card.querySelector<HTMLElement>('.bsa-login-rim');
    const panel = card.querySelector<HTMLElement>('[data-login-panel]');
    const spot = card.querySelector<HTMLElement>('.bsa-login-spot');
    const wraps = [...card.querySelectorAll<HTMLElement>('.bsa-login-streakwrap')];

    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(tiltFrame.current);
      tiltFrame.current = requestAnimationFrame(() => {
        const rect = stage.getBoundingClientRect();
        const rx = -((event.clientY - rect.top) / rect.height - 0.5) * 5;
        const ry = ((event.clientX - rect.left) / rect.width - 0.5) * 6;
        card.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;

        // The rim catches the light where the cursor actually is.
        const cardRect = card.getBoundingClientRect();
        if (rim) {
          rim.style.setProperty('--spot-x', `${event.clientX - cardRect.left}px`);
          rim.style.setProperty('--spot-y', `${event.clientY - cardRect.top}px`);
          rim.style.opacity = '1';
        }

        // Inside the gradient panel: the highlight rides the cursor, and each
        // streak is pushed off its line and brightened as the cursor nears.
        if (panel) {
          const pr = panel.getBoundingClientRect();
          const px = event.clientX - pr.left;
          const py = event.clientY - pr.top;
          const inside = px >= 0 && py >= 0 && px <= pr.width && py <= pr.height;
          if (spot) {
            spot.style.opacity = inside ? '1' : '0';
            spot.style.transform = `translate3d(${px - 170}px, ${py - 170}px, 0)`;
          }
          for (const wrap of wraps) {
            const cx = (Number(wrap.dataset.x) / 100) * pr.width;
            const cy = (Number(wrap.dataset.y) / 100) * pr.height;
            const dx = cx - px;
            const dy = cy - py;
            const dist = Math.hypot(dx, dy);
            const strength = inside ? Math.max(0, 1 - dist / 300) : 0;
            if (strength > 0.01) {
              const push = 30 * strength;
              wrap.style.transform = `translate3d(${((dx / (dist || 1)) * push).toFixed(1)}px, ${((dy / (dist || 1)) * push).toFixed(1)}px, 0)`;
              wrap.style.opacity = String(
                Math.min(1, Number(wrap.dataset.o) + strength * 0.45),
              );
              wrap.style.filter = `brightness(${(1 + strength * 0.9).toFixed(2)})`;
            } else {
              wrap.style.transform = 'translate3d(0, 0, 0)';
              wrap.style.opacity = wrap.dataset.o ?? '1';
              wrap.style.filter = 'brightness(1)';
            }
          }
        }
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(tiltFrame.current);
      card.style.transform = 'rotateX(0deg) rotateY(0deg)';
      if (rim) rim.style.opacity = '0';
      if (spot) spot.style.opacity = '0';
      for (const wrap of wraps) {
        wrap.style.transform = 'translate3d(0, 0, 0)';
        wrap.style.opacity = wrap.dataset.o ?? '1';
        wrap.style.filter = 'brightness(1)';
      }
    };
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(tiltFrame.current);
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  /** Where they were headed before the guard sent them here. */
  const from = useMemo(() => {
    const state = location.state as { from?: unknown } | null;
    const target = state?.from;
    // Only ever an in-app path, so a crafted `state` cannot bounce anyone off-site.
    return typeof target === 'string' &&
      target.startsWith('/') &&
      !target.startsWith('//') &&
      target !== '/login'
      ? target
      : null;
  }, [location.state]);

  useEffect(() => {
    if (mode === 'email') emailRef.current?.focus();
  }, [mode]);

  // While the operator types, pull the chunks the landing page will need the
  // moment sign-in succeeds — the dashboard then paints without a network pause.
  useEffect(() => {
    const warm = () => void import('@/features/dashboard/DashboardPage');
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(warm, { timeout: 2000 });
      return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(warm, 400);
    return () => window.clearTimeout(handle);
  }, []);

  // Move the reader to the failure rather than hoping they notice it.
  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError]);

  const errors: FormErrors = useMemo(() => {
    const next: FormErrors = {};
    const trimmed = email.trim();
    if (!trimmed) next.email = 'Enter your email address or username.';
    // A username has no @; only address-shaped input is held to the pattern.
    else if (trimmed.includes('@') && !EMAIL_PATTERN.test(trimmed))
      next.email = 'That does not look like an email address.';
    if (!password) next.password = 'Enter your password.';
    return next;
  }, [email, password]);

  const values = { email, password };

  /**
   * Reward early, punish late. Before a submit attempt, only a field with
   * something in it can be wrong — the email field auto-focuses, so merely
   * clicking elsewhere must never raise "Enter your email address." on an
   * untouched form. Emptiness becomes an error only when sign-in is asked for.
   */
  const showError = (field: keyof FormErrors): string | undefined => {
    if (submitted) return errors[field];
    return touched[field] && values[field] ? errors[field] : undefined;
  };

  async function submitPin(candidate: string, person: PinIdentity | null) {
    if (pinPendingRef.current) return;
    pinPendingRef.current = true;
    setPending(true);
    setSubmitError(null);
    try {
      if (person) {
        await loginWithUserPin(person.id, candidate);
      } else {
        await loginWithPin(desk, candidate);
      }
      navigate(from ?? '/', { replace: true });
    } catch (error) {
      setSubmitError(toSubmitError(error));
      setPin('');
    } finally {
      pinPendingRef.current = false;
      setPending(false);
    }
  }

  function pressDigit(digit: string) {
    if (pending) return;
    setSubmitError(null);
    setPin((current) => {
      if (current.length >= PIN_LENGTH) return current;
      const next = current + digit;
      if (next.length === PIN_LENGTH) void submitPin(next, mode === 'pin' ? identity : null);
      return next;
    });
  }

  function pressBackspace() {
    if (pending) return;
    setPin((current) => current.slice(0, -1));
  }

  // Physical keyboards work on the keypad too: digits type, backspace deletes,
  // escape steps back to the credentials form.
  useEffect(() => {
    if (mode === 'email' || (mode === 'pin' && !identity)) return;
    const onKey = (event: KeyboardEvent) => {
      if (/^[0-9]$/.test(event.key)) pressDigit(event.key);
      else if (event.key === 'Backspace') pressBackspace();
      else if (event.key === 'Escape') {
        if (mode === 'pin') chooseIdentity(null);
        else setMode('pin');
        setPin('');
        setSubmitError(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pending, identity]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
    setSubmitError(null);

    if (errors.email || errors.password) return;

    setPending(true);
    try {
      await login(email, password);
      navigate(from ?? '/', { replace: true });
    } catch (error) {
      setSubmitError(toSubmitError(error));
      // Clear the password rather than leave a rejected one in the field.
      setPassword('');
      setSubmitted(false);
      setTouched((current) => ({ ...current, password: false }));
    } finally {
      setPending(false);
    }
  }

  function fillDemoCredentials() {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    setSubmitError(null);
    setSubmitted(false);
    setTouched({ email: false, password: false });
  }

  // A live session has no business on this screen — send it where it was going.
  if (!isLoading && isAuthenticated) return <Navigate to={from ?? '/'} replace />;

  const unlockAt = submitError?.lockedUntil ? formatUnlockTime(submitError.lockedUntil) : null;

  return (
    /*
     * The sign-in scene, fourth pass (owner's reference): a deep night page,
     * one floating card. The left panel is the only place the product speaks
     * in gradient — brand blue falling into violet, warm light-streaks like
     * tail-lights leaving the depot. The right panel is the working face, and
     * everything on it is the same machine as before: credentials first, one
     * head-admin PIN door behind it.
     */
    <div
      className="relative flex min-h-dvh flex-col overflow-hidden"
      style={{
        background:
          'radial-gradient(1100px 640px at 78% -10%, color-mix(in oklab, var(--login-g3) 26%, transparent), transparent 62%), radial-gradient(900px 620px at 8% 108%, color-mix(in oklab, var(--login-g1) 22%, transparent), transparent 60%), linear-gradient(180deg, var(--login-night) 0%, var(--login-night-deep) 100%)',
      }}
    >
      <header className="relative z-10 flex items-center justify-between gap-4 px-5 py-4 sm:px-8">
        <div className="flex min-w-0 items-center gap-2.5">
          <img
            src={brandMark}
            alt=""
            aria-hidden
            width={480}
            height={480}
            decoding="async"
            className="h-9 w-9 shrink-0 rounded-control object-cover"
          />
          <div className="min-w-0 leading-tight">
            <p className="truncate text-body font-semibold text-white">Bright Stock Analyzer</p>
            <p className="truncate font-data text-label tracking-[0.14em] text-white/50 uppercase">
              Bright Autozone
            </p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={toggle}
          className="text-white/80 hover:text-white"
          aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
      </header>

      {/* The night's own weather: two brand glows, breathing slowly. */}
      <div
        aria-hidden
        className="bsa-login-glow pointer-events-none absolute -top-40 right-[-10%] z-0 h-[560px] w-[560px] rounded-full"
        style={{
          background:
            'radial-gradient(circle, color-mix(in oklab, var(--login-g3) 34%, transparent), transparent 68%)',
        }}
      />
      <div
        aria-hidden
        className="bsa-login-glow pointer-events-none absolute bottom-[-24%] left-[-8%] z-0 h-[620px] w-[620px] rounded-full"
        style={{
          animationDelay: '-4.5s',
          background:
            'radial-gradient(circle, color-mix(in oklab, var(--login-g1) 30%, transparent), transparent 66%)',
        }}
      />

      <main className="bsa-login-stage relative z-10 flex flex-1 items-center justify-center px-4 pb-10 sm:px-8">
        <div
          ref={cardRef}
          className="bsa-login-card relative grid w-full max-w-[1000px] overflow-hidden rounded-[26px] bg-surface shadow-lg lg:grid-cols-[1.08fr_1fr]"
        >
          <span aria-hidden className="bsa-login-rim" />
          {/* The gradient panel — brand blue into violet, streaks rising. */}
          <aside
            aria-hidden
            data-login-panel
            className="relative hidden min-h-[580px] flex-col overflow-hidden p-10 lg:flex"
            style={{
              background:
                'linear-gradient(150deg, var(--login-g1) 0%, var(--login-g2) 46%, var(--login-g3) 100%)',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Depth: streaks recessed behind the glass... */}
            <div style={{ transform: 'translateZ(-40px) scale(1.07)' }} className="absolute inset-0">
              <LoginStreaks />
            </div>

            {/* The highlight that rides the cursor. */}
            <span className="bsa-login-spot" />

            {/* The wings themselves, flying: an extruded 3D mark that arrives
                from deep in the scene, then banks and hovers. The plates give
                it thickness the moment the card tilts. */}
            <div
              className="relative flex flex-1 items-center justify-center"
              style={{ transform: 'translateZ(46px)', transformStyle: 'preserve-3d' }}
            >
              <span
                aria-hidden
                className="bsa-logo-glowdisc pointer-events-none absolute h-[240px] w-[420px] rounded-full"
              />
              <div className="bsa-logo-arrive w-[78%] max-w-[400px]">
                <div
                  className="bsa-logo-stack"
                  style={{ ['--logo-url' as string]: `url(${citycabLogo})` }}
                >
                    {[10, 8, 6, 4, 2].map((depth, index) => (
                      <img
                        key={depth}
                        src={citycabLogo}
                        alt=""
                        aria-hidden
                        width={808}
                        height={400}
                        decoding="async"
                        className="bsa-logo-plate"
                        style={{
                          transform: `translateZ(-${depth}px)`,
                          opacity: 0.16 + index * 0.05,
                        }}
                      />
                    ))}
                    <img
                      src={citycabLogo}
                      alt="City Cab"
                      width={808}
                      height={400}
                      decoding="async"
                      className="relative drop-shadow-[0_18px_28px_rgba(7,11,26,0.5)]"
                    />
                    <span aria-hidden className="bsa-logo-shine" />
                </div>
              </div>
            </div>

            {/* The line, under the flight. */}
            <div style={{ transform: 'translateZ(24px)' }} className="relative mb-12">
              <h2 className="font-display text-[40px] leading-[1.04] font-semibold tracking-tight text-white">
                Every chassis,
                <br />
                one book.
              </h2>
            </div>

            <p
              className="absolute bottom-8 left-10 font-data text-micro tracking-[0.16em] text-white/60 uppercase"
              style={{ transform: 'translateZ(12px)' }}
            >
              City Cab · Electric Rickshaws · citycabev.com
            </p>
          </aside>

          {/* The working face. */}
          <div className="flex min-w-0 flex-col justify-center px-6 py-8 sm:px-10 sm:py-10">
            {/* On phones the gradient arrives as a slim banner instead of a panel. */}
            <div
              className="relative mb-6 flex items-center justify-between overflow-hidden rounded-2xl px-4 py-3 lg:hidden"
              style={{
                background:
                  'linear-gradient(135deg, var(--login-g1), var(--login-g2) 60%, var(--login-g3))',
              }}
            >
              <span className="relative z-10 text-label font-semibold text-white">
                Every chassis, one book.
              </span>
              <img
                src={citycabLogo}
                alt="City Cab"
                width={808}
                height={400}
                decoding="async"
                className="relative z-10 h-10 w-auto"
              />
            </div>
            <div className="w-full">
            <h1
              className="bsa-login-item font-display text-figure leading-tight font-semibold text-ink sm:text-figure"
              style={{ ['--i' as string]: 0 }}
            >
              Sign in
            </h1>
            <p
              className="bsa-login-item mt-2 text-body leading-relaxed text-muted"
              style={{ ['--i' as string]: 1 }}
            >
              {mode === 'admin'
                ? desk === CEO_DESK
                  ? 'Enter your CEO desk PIN.'
                  : 'Enter the head admin PIN. Switch books from the top once you are in.'
                : mode === 'pin'
                  ? identity
                    ? 'Enter your four-digit PIN.'
                    : 'Find your name — your shop or your town — then enter your PIN.'
                  : from
                    ? 'Your session has ended. Sign in and you will be taken back where you were.'
                    : 'Stock, custody and ageing across the dealer network.'}
            </p>

            {from && from !== '/' ? (
              <p className="mt-3 flex min-w-0 flex-wrap items-baseline gap-x-2 text-micro text-muted">
                <span>Continuing to</span>
                <Mono value={from} size="sm" subdued truncate />
              </p>
            ) : null}

            {submitError ? (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                className={clsx(
                  'mt-6 flex gap-2.5 rounded-md border px-3.5 py-3 text-micro leading-relaxed',
                  submitError.tone === 'blocked'
                    ? 'border-signal bg-signal-soft text-ink'
                    : 'border-danger bg-surface text-ink',
                )}
              >
                <AlertTriangle
                  aria-hidden
                  className={clsx(
                    'mt-0.5 h-4 w-4 shrink-0',
                    submitError.tone === 'blocked' ? 'text-signal' : 'text-danger',
                  )}
                />
                <div className="min-w-0">
                  <p>{submitError.message}</p>
                  {unlockAt ? (
                    <p className="mt-1 font-data text-micro text-muted tabular-nums">
                      Unlocks at {unlockAt}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {mode === 'admin' ? (
              <div className="mt-6 flex flex-col gap-5">
                <button
                  type="button"
                  onClick={() => {
                    setMode('pin');
                    setPin('');
                    setSubmitError(null);
                  }}
                  className="flex items-center gap-1.5 self-start text-micro font-medium text-muted hover:text-ink"
                >
                  <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
                  Back to sign in
                </button>

                {/* One desk (3 Oct 2026): Bright, the head admin. The book is chosen inside the app. */}
                <div className="flex items-center justify-center gap-2 rounded-control border border-accent bg-accent-soft px-3 py-2.5 text-accent">
                  {desk === CEO_DESK ? <Gauge aria-hidden className="h-4 w-4" /> : <ShieldCheck aria-hidden className="h-4 w-4" />}
                  <span className="text-label font-medium">{desk === CEO_DESK ? 'Sunny · CEO' : 'Bright · Head admin'}</span>
                </div>

                <PinPad pin={pin} pending={pending} onDigit={pressDigit} onBackspace={pressBackspace} />

                <p className="text-center text-micro text-muted" aria-live="polite">
                  {pending ? 'Signing in…' : 'The PIN signs you straight in.'}
                </p>
              </div>
            ) : null}

            {mode === 'pin' ? (
              <div className="mt-6 flex flex-col gap-5">
                {identity ? (
                  <>
                    {/* Who the phone remembers — one line, one way out. */}
                    <div className="flex items-center gap-3 rounded-plane border border-line bg-surface-2 px-4 py-3">
                      <span
                        aria-hidden
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-accent-soft text-accent"
                      >
                        <UserRound className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-label font-semibold text-ink">{identity.name}</span>
                        <span className="block truncate text-micro text-muted">
                          {[identity.label, identity.where, identity.book].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => chooseIdentity(null)}>
                        Not you?
                      </Button>
                    </div>

                    <PinPad pin={pin} pending={pending} onDigit={pressDigit} onBackspace={pressBackspace} />

                    <p className="text-center text-micro text-muted" aria-live="polite">
                      {pending ? 'Signing in…' : 'Four digits and you are in.'}
                    </p>
                  </>
                ) : (
                  <>
                    <Input
                      ref={whoRef}
                      label="Who are you?"
                      type="text"
                      value={who}
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      placeholder="Your name, your shop, or your town"
                      leadingIcon={<Search aria-hidden className="h-4 w-4 text-muted" />}
                      onChange={(event) => setWho(event.target.value)}
                    />
                    <div className="max-h-72 overflow-y-auto rounded-md border border-line" aria-live="polite">
                      {who.trim().length < 2 ? (
                        <p className="px-3 py-3 text-micro text-muted">
                          Type two letters of your name and the list will come.
                        </p>
                      ) : searching && matches.length === 0 ? (
                        <p className="px-3 py-3 text-micro text-muted">Looking…</p>
                      ) : matches.length === 0 ? (
                        <p className="px-3 py-3 text-micro text-muted">
                          No one by that name has a PIN yet. Ask the office to set one, or sign in with your username below.
                        </p>
                      ) : (
                        <ul className="flex flex-col">
                          {matches.map((hit) => (
                            <li key={hit.id}>
                              <button
                                type="button"
                                onClick={() => chooseIdentity(hit)}
                                className="flex min-h-tap w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150 hover:bg-surface-2"
                              >
                                <span
                                  aria-hidden
                                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-surface-2 text-muted"
                                >
                                  <UserRound className="h-4 w-4" />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-label font-medium text-ink">{hit.name}</span>
                                  <span className="block truncate text-micro text-muted">
                                    {[hit.label, hit.where, hit.book].filter(Boolean).join(' · ')}
                                  </span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPin('');
                      setSubmitError(null);
                      setDesk(CEO_DESK);
                      setMode('admin');
                    }}
                    className="col-span-2 flex items-center gap-2.5 rounded-plane border border-line bg-surface px-3 py-2.5 text-left shadow-sm transition-transform duration-150 hover:border-accent active:translate-y-px"
                  >
                    <Gauge aria-hidden className="h-4 w-4 shrink-0 text-accent" />
                    <span className="min-w-0">
                      <span className="block text-label font-medium text-ink">CEO</span>
                      <span className="block truncate text-micro text-muted">Sunny · the business on one page</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPin('');
                      setSubmitError(null);
                      setDesk(HEAD_ADMIN_DESK);
                      setMode('admin');
                    }}
                    className="flex items-center gap-2.5 rounded-plane border border-line bg-surface px-3 py-2.5 text-left shadow-sm transition-transform duration-150 hover:border-accent active:translate-y-px"
                  >
                    <ShieldCheck aria-hidden className="h-4 w-4 shrink-0 text-accent" />
                    <span className="min-w-0">
                      <span className="block text-label font-medium text-ink">Head admin</span>
                      <span className="block truncate text-micro text-muted">Bright · PIN</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitError(null);
                      setMode('email');
                    }}
                    className="flex items-center gap-2.5 rounded-plane border border-line bg-surface px-3 py-2.5 text-left shadow-sm transition-transform duration-150 hover:border-accent active:translate-y-px"
                  >
                    <KeyRound aria-hidden className="h-4 w-4 shrink-0 text-accent" />
                    <span className="min-w-0">
                      <span className="block text-label font-medium text-ink">Username</span>
                      <span className="block truncate text-micro text-muted">Email or username and password</span>
                    </span>
                  </button>
                </div>
              </div>
            ) : null}

            {mode === 'email' ? (
              <>
            <form onSubmit={handleSubmit} noValidate className="mt-6 flex flex-col gap-4">
              <Input
                ref={emailRef}
                label="Email or username"
                type="text"
                name="email"
                value={email}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="you@brightonwheels.in — or your outlet username"
                error={showError('email')}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setTouched((current) => ({ ...current, email: true }))}
              />

              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                autoComplete="current-password"
                placeholder="Your password"
                error={showError('password')}
                hint={capsLock ? 'Caps Lock is on.' : undefined}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={(event) => setCapsLock(event.getModifierState('CapsLock'))}
                onKeyUp={(event) => setCapsLock(event.getModifierState('CapsLock'))}
                onBlur={() => {
                  setCapsLock(false);
                  setTouched((current) => ({ ...current, password: true }));
                }}
                trailing={
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                }
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={pending}
                className="rounded-pill border-0"
                style={{
                  background: 'linear-gradient(135deg, var(--login-g1), var(--login-g3))',
                }}
              >
                {pending ? 'Signing in' : 'Sign in'}
              </Button>
            </form>

            {/*
              Demo credentials. Marked as such and kept quiet so they do not compete
              with the form, but present — hunting for them wastes a reviewer's time.
              Dev builds only: the live site must not display credentials, working
              or not.
            */}
            {import.meta.env.DEV ? (
            <Card className="mt-8 border-dashed bg-surface-2 shadow-none">
              <div className="flex flex-col gap-3 px-4 py-3.5">
                <div className="flex items-center gap-2">
                  <ShieldCheck aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted" />
                  <span className="font-data text-label font-medium tracking-[0.16em] text-muted uppercase">
                    Demo install
                  </span>
                </div>

                <dl className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-micro">
                  <dt className="text-muted">Email</dt>
                  <dd className="min-w-0">
                    <Mono value={DEMO_EMAIL} size="sm" copyable label="demo email address" />
                  </dd>
                  <dt className="text-muted">Password</dt>
                  <dd className="min-w-0">
                    <Mono value={DEMO_PASSWORD} size="sm" copyable label="demo password" />
                  </dd>
                </dl>

                <div>
                  <Button size="sm" onClick={fillDemoCredentials}>
                    Fill these in
                  </Button>
                </div>
              </div>
            </Card>
            ) : null}

                {/* Back to everyone's door — the PIN is the default face now. */}
                <button
                  type="button"
                  onClick={() => {
                    setSubmitError(null);
                    setMode('pin');
                  }}
                  className="mt-6 flex w-full items-center gap-3 rounded-plane border border-line bg-surface px-4 py-3 text-left shadow-sm transition-transform duration-150 hover:border-accent hover:shadow-md active:translate-y-px"
                >
                  <span
                    aria-hidden
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-accent-soft text-accent"
                  >
                    <UserRound className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-label font-semibold text-ink">Sign in with your PIN</span>
                    <span className="block truncate text-micro text-muted">
                      Find your name, tap four digits — mitrs, outlets, ASMs and the head admin
                    </span>
                  </span>
                </button>
              </>
            ) : null}
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 pb-5 text-center text-micro text-white/45">
        Trouble signing in? Ask an administrator to reset your password.
      </footer>
    </div>
  );
}

/**
 * The reference's comet streaks, drawn in CSS: soft warm capsules rising
 * through the gradient at one angle, like tail-lights leaving the depot at
 * dusk. Pure decoration, pure transform — nothing animates.
 */
function LoginStreaks() {
  const streaks: Array<{ w: number; x: number; y: number; c: string; o: number; dur: number; delay: number }> = [
    { w: 150, x: -30, y: 78, c: 'var(--login-streak-a)', o: 0.9, dur: 11, delay: 0 },
    { w: 90, x: 16, y: 84, c: 'var(--login-streak-b)', o: 0.75, dur: 14, delay: -6 },
    { w: 210, x: 26, y: 92, c: 'var(--login-streak-c)', o: 0.6, dur: 12, delay: -3 },
    { w: 120, x: 52, y: 72, c: 'var(--login-streak-a)', o: 0.5, dur: 16, delay: -9 },
    { w: 70, x: 66, y: 88, c: 'var(--login-streak-b)', o: 0.65, dur: 10, delay: -2 },
    { w: 170, x: -8, y: 62, c: 'var(--login-streak-c)', o: 0.35, dur: 18, delay: -12 },
    { w: 110, x: 44, y: 56, c: 'var(--login-streak-b)', o: 0.28, dur: 15, delay: -7 },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {streaks.map((streak, index) => (
        <span
          key={index}
          className="bsa-login-streakwrap"
          data-x={streak.x}
          data-y={streak.y}
          data-o={streak.o}
          style={{
            width: streak.w,
            height: 14,
            left: `${streak.x}%`,
            top: `${streak.y}%`,
            opacity: streak.o,
          }}
        >
          <span
            className="bsa-login-streak absolute inset-0 rounded-full"
            style={{
              background: `linear-gradient(90deg, transparent, ${streak.c})`,
              filter: 'blur(0.5px)',
              ['--dur' as string]: `${streak.dur}s`,
              ['--delay' as string]: `${streak.delay}s`,
            }}
          />
        </span>
      ))}
      <span
        className="absolute -right-16 -top-24 h-72 w-72 rounded-full"
        style={{
          background:
            'radial-gradient(circle, color-mix(in oklab, white 26%, transparent), transparent 70%)',
        }}
      />
    </div>
  );
}
